import React, { useState } from "react";
import useLogosQuery from "../hooks/useLogosQuery";
import useFileUploadMutation from "../hooks/useFileUploadMutation";
import useCreateLogoMutation from "../hooks/useCreateLogoMutation";
import { PendulumLogo } from "../../../../api/giftCategories";
import FloatAddButton from "../../../../components/Widgets/FloatAddButton";
import LogoModal from "./LogoModal";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSAlert from "../../../system-design/DSAlert";
import DSBox from "../../../system-design/DSBox";
import DSStack from "../../../system-design/DSStack";
import DSTypography from "../../../system-design/DSTypography";
import DSTextField from "../../../system-design/DSTextField";
import DSCard from "../../../system-design/DSCard";
import DSCardContent from "../../../system-design/DSCardContent";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSUploadButton from "../../../system-design/DSUploadButton";

// Extract UploadModal as a separate component to prevent re-renders
const UploadModal = ({
  show,
  onHide,
  showError,
  selectedFile,
  uploadedUrl,
  logoName,
  logoDescription,
  isFileUploading,
  isCreatingLogo,
  handleFileSelect,
  handleFileUpload,
  handleCreateLogo,
  onCancel,
  setLogoName,
  setLogoDescription,
}: {
  show: boolean;
  onHide: () => void;
  showError: boolean;
  selectedFile: string;
  uploadedUrl: string;
  logoName: string;
  logoDescription: string;
  isFileUploading: boolean;
  isCreatingLogo: boolean;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileUpload: () => void;
  handleCreateLogo: () => void;
  onCancel: () => void;
  setLogoName: (name: string) => void;
  setLogoDescription: (description: string) => void;
}) => (
  <DSDialog open={show} onClose={onHide} fullWidth maxWidth="md">
    <DSDialogTitle>Upload New Pendulum Logo</DSDialogTitle>
    <DSDialogContent>
      {showError && (
        <DSAlert severity="error" sx={{ mb: 2 }}>
          {!selectedFile
            ? "Please select an image file to upload."
            : "Please upload the file first."}
        </DSAlert>
      )}
      <form>
        <DSStack spacing={2} sx={{ mt: 1 }}>
          <DSBox>
            <DSTypography sx={{ mb: 1 }}>Select Logo Image</DSTypography>
            <label>
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileSelect}
                disabled={!!uploadedUrl}
              />
              <DSUploadButton
                text="Select Logo Image"
                disabled={!!uploadedUrl}
              />
            </label>
          </DSBox>

          {selectedFile && (
            <DSBox>
              <DSTypography sx={{ mb: 1 }}>Preview:</DSTypography>
              <DSBox sx={{ textAlign: "center" }}>
                <img
                  src={selectedFile}
                  alt="Preview"
                  style={{ maxWidth: "200px", maxHeight: "200px" }}
                />
              </DSBox>
            </DSBox>
          )}

          {uploadedUrl && (
            <>
              <DSAlert severity="success">File uploaded successfully!</DSAlert>

              <DSTextField
                label="Logo Name (Optional)"
                value={logoName}
                onChange={(e) => setLogoName(e.target.value)}
                placeholder="Enter a name for this logo..."
                fullWidth
              />

              <DSTextField
                label="Description (Optional)"
                multiline
                rows={3}
                value={logoDescription}
                onChange={(e) => setLogoDescription(e.target.value)}
                placeholder="Enter a description for this logo..."
                fullWidth
              />
            </>
          )}
        </DSStack>
      </form>
    </DSDialogContent>
    <DSDialogActions>
      <DSSecondaryButton onClick={onCancel}>Cancel</DSSecondaryButton>
      {uploadedUrl ? (
        <DSPrimaryButton onClick={handleCreateLogo} disabled={isCreatingLogo}>
          {isCreatingLogo ? "Creating..." : "Create Logo"}
        </DSPrimaryButton>
      ) : (
        <DSPrimaryButton
          onClick={handleFileUpload}
          disabled={!selectedFile || isFileUploading}
        >
          {isFileUploading ? "Uploading..." : "Upload & Continue"}
        </DSPrimaryButton>
      )}
    </DSDialogActions>
  </DSDialog>
);

const Logos = () => {
  const { data: logos, isLoading } = useLogosQuery();
  const { mutate: uploadFile, isPending: isFileUploading } =
    useFileUploadMutation();
  const { mutate: createLogo, isPending: isCreatingLogo } =
    useCreateLogoMutation();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [fileName, setFileName] = useState("");
  const [uploadedUrl, setUploadedUrl] = useState<string>("");
  const [logoName, setLogoName] = useState<string>("");
  const [logoDescription, setLogoDescription] = useState<string>("");
  const [showError, setShowError] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState<PendulumLogo | null>(null);

  const resetModalState = () => {
    setSelectedFile("");
    setFileName("");
    setUploadedUrl("");
    setLogoName("");
    setLogoDescription("");
    setShowError(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setSelectedFile(fileReader.result as string);
        setFileName(file.name);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleFileUpload = () => {
    if (!selectedFile) {
      setShowError(true);
      return;
    }

    uploadFile(
      { file: selectedFile, fileName },
      {
        onSuccess: (response) => {
          setUploadedUrl(response.url);
          setShowError(false);
        },
      },
    );
  };

  const handleCreateLogo = () => {
    if (!uploadedUrl) {
      setShowError(true);
      return;
    }

    createLogo(
      {
        url: uploadedUrl,
        filename: fileName,
        name: logoName || undefined,
        description: logoDescription || undefined,
      },
      {
        onSuccess: () => {
          setShowUploadModal(false);
          resetModalState();
        },
      },
    );
  };

  if (isLoading) {
    return <DSTypography>Loading logos...</DSTypography>;
  }

  return (
    <>
      <DSBox sx={{ mb: 4 }}>
        <DSTypography variant="h6">Pendulum Logos</DSTypography>
        <DSTypography color="text.secondary">
          Manage pendulum logos for gifts. Click the + button to upload a new
          logo.
        </DSTypography>
      </DSBox>

      <DSBox
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xxs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 3,
        }}
      >
        {logos?.map((logo: PendulumLogo) => (
          <DSCard
            key={logo.id}
            sx={{ height: "100%", cursor: "pointer" }}
            onClick={() => setSelectedLogo(logo)}
          >
            <DSBox
              sx={{
                height: "200px",
                p: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={logo.url}
                alt={logo.filename}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </DSBox>
            <DSCardContent>
              <DSTypography
                variant="h6"
                noWrap
                title={logo.name || logo.filename}
              >
                {logo.name || logo.filename}
              </DSTypography>
              {logo.description && (
                <DSTypography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: "block" }}
                >
                  {logo.description}
                </DSTypography>
              )}
              <DSTypography variant="caption" color="text.secondary">
                Uploaded: {new Date(logo.createdAt).toLocaleDateString()}
              </DSTypography>
            </DSCardContent>
          </DSCard>
        ))}
      </DSBox>

      {logos?.length === 0 && (
        <DSBox sx={{ textAlign: "center", py: 5 }}>
          <DSTypography color="text.secondary" sx={{ mb: 2 }}>
            No logos uploaded yet.
          </DSTypography>
          <DSPrimaryButton onClick={() => setShowUploadModal(true)}>
            Upload First Logo
          </DSPrimaryButton>
        </DSBox>
      )}

      <FloatAddButton onClick={() => setShowUploadModal(true)} />
      <UploadModal
        show={showUploadModal}
        onHide={() => {
          setShowUploadModal(false);
          resetModalState();
        }}
        showError={showError}
        selectedFile={selectedFile}
        uploadedUrl={uploadedUrl}
        logoName={logoName}
        logoDescription={logoDescription}
        isFileUploading={isFileUploading}
        isCreatingLogo={isCreatingLogo}
        handleFileSelect={handleFileSelect}
        handleFileUpload={handleFileUpload}
        handleCreateLogo={handleCreateLogo}
        onCancel={() => {
          setShowUploadModal(false);
          resetModalState();
        }}
        setLogoName={setLogoName}
        setLogoDescription={setLogoDescription}
      />

      {selectedLogo && (
        <LogoModal logo={selectedLogo} onClose={() => setSelectedLogo(null)} />
      )}
    </>
  );
};

export default Logos;
